import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
var _templateObject;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useMutation } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { useRef } from 'react';
import { isFedRamp } from '@atlassian/atl-context';
import { ForgeCDNEnvironment } from '@atlassian/forge-ui-types';
import { useTracingContext } from '../../../error-reporting';
import { base64ToBlob, blobToBase64, getCDNEnvironment } from '../../../utils';
export var authTokenForExtensionMutation = gql(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n\tmutation forge_ui_userAuthTokenForExtension($input: UserAuthTokenForExtensionInput!) {\n\t\tuserAuthTokenForExtension(input: $input) {\n\t\t\tsuccess\n\t\t\terrors {\n\t\t\t\tmessage\n\t\t\t}\n\t\t\tauthToken {\n\t\t\t\ttoken\n\t\t\t\tttl\n\t\t\t}\n\t\t}\n\t}\n"])));
var validProducts = ['confluence', 'jira', 'bitbucket'];
var apiGatewayUrlMap = {
  dev: 'api.stg.atlassian.com',
  stg: 'api.stg.atlassian.com',
  prod: 'api.atlassian.com',
  fex: 'api.atlassian-fex.com'
};
var getApiGatewayUrl = function getApiGatewayUrl(environment) {
  var apiBase = apiGatewayUrlMap[environment];
  var url = isFedRamp() && environment !== ForgeCDNEnvironment.FEDRAMP_SANDBOX ? apiBase.replace('atlassian.com', 'atlassian-us-gov-mod.com') : apiBase;
  if (!url) {
    throw new Error("Invalid environment: ".concat(environment));
  }
  return url;
};
function getBaseUrl(environment, product, cloudId) {
  if (product === 'bitbucket') {
    return environment === 'prod' || environment === 'fex' ? 'https://api.bitbucket.org' : 'https://api.integration.bb-inf.net';
  }
  var apiBase = getApiGatewayUrl(environment);
  return "https://".concat(apiBase, "/ex/").concat(product, "/").concat(cloudId);
}
function createProductUrl(environment, product, restPath, cloudId) {
  var baseUrl = getBaseUrl(environment, product, cloudId);
  var fullUrl =
  // bitbucket requests could be made with absolute URLs
  // e.g. when following href links in API responses
  product === 'bitbucket' && restPath.startsWith("".concat(baseUrl, "/")) ? restPath : "".concat(baseUrl).concat(restPath);
  var productUrl = new URL(fullUrl).toString();
  if (!productUrl.startsWith("".concat(baseUrl, "/"))) {
    throw new Error('Invalid product URL');
  }
  return productUrl;
}
function isTokenValid(token) {
  return !!(token && token.expiry > Date.now());
}
function isProduct(product) {
  return validProducts.includes(product);
}
export function isBinaryContentType(contentType) {
  return Boolean(contentType && !contentType.includes('application/json') && contentType.search(/text\//) === -1);
}
function parseFormData(formLike) {
  var form = new FormData();
  for (var _i = 0, _Object$entries = Object.entries(formLike); _i < _Object$entries.length; _i++) {
    var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
      key = _Object$entries$_i[0],
      value = _Object$entries$_i[1];
    if (key === 'file') {
      var fileName = formLike['__fileName'];
      var mimeType = formLike['__fileType'];

      // convert back from base64 to File (Blob)
      var blob = base64ToBlob(value, mimeType);
      var file = new File([blob], fileName, {
        type: mimeType
      });
      form.append('file', file);
    } else {
      form.append(key, value);
    }
  }

  // remove request metadata properties
  form.delete('__fileName');
  form.delete('__fileType');
  return form;
}
var MILLIS_IN_SECOND = 1000;
export var requestProduct = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(url, init) {
    var response, isAttachment, b64Content;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          _context.next = 2;
          return fetch(url, init);
        case 2:
          response = _context.sent;
          isAttachment = isBinaryContentType(response.headers.get('content-type'));
          if (!(response.ok && response.body !== null && response.status !== 204 && isAttachment)) {
            _context.next = 13;
            break;
          }
          _context.t0 = blobToBase64;
          _context.next = 8;
          return response.blob();
        case 8:
          _context.t1 = _context.sent;
          _context.next = 11;
          return (0, _context.t0)(_context.t1);
        case 11:
          b64Content = _context.sent;
          return _context.abrupt("return", new Response(b64Content, {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers
          }));
        case 13:
          return _context.abrupt("return", response);
        case 14:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return function requestProduct(_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();
export function useProductFetchClient(_ref2) {
  var extensionId = _ref2.extensionId,
    accountType = _ref2.accountType,
    environment = _ref2.environment,
    cloudId = _ref2.cloudId,
    workspaceId = _ref2.workspaceId,
    renderThreeLOPrompt = _ref2.renderThreeLOPrompt,
    options = _ref2.options,
    _ref2$contextIds = _ref2.contextIds,
    contextIds = _ref2$contextIds === void 0 ? [] : _ref2$contextIds;
  var _useMutation = useMutation(authTokenForExtensionMutation, options),
    _useMutation2 = _slicedToArray(_useMutation, 1),
    mutationFunction = _useMutation2[0];
  var authTokenRef = useRef(undefined);
  var tracing = useTracingContext();

  /**
   * Workspace based products like Trello and Bitbucket will not pass in cloudId,
   * they will pass in workspaceId instead.
   */
  if (!cloudId && !workspaceId) {
    return /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            throw new Error('Product APIs are not available in this module. Remove requestConfluence, requestJira or requestBitbucket from your code.');
          case 1:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }));
  }
  var fetchOAuthToken = /*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(extensionId, contextIds) {
      var _yield$mutationFuncti, data, _data$userAuthTokenFo, success, authToken, errors, token, ttl;
      return _regeneratorRuntime.wrap(function _callee4$(_context4) {
        while (1) switch (_context4.prev = _context4.next) {
          case 0:
            tracing === null || tracing === void 0 || tracing.recordGqlCall('userAuthTokenForExtension - start');
            _context4.next = 3;
            return mutationFunction({
              variables: {
                input: {
                  extensionId: extensionId,
                  contextIds: contextIds
                }
              }
            });
          case 3:
            _yield$mutationFuncti = _context4.sent;
            data = _yield$mutationFuncti.data;
            if (!(data !== null && data !== void 0 && data.userAuthTokenForExtension)) {
              _context4.next = 13;
              break;
            }
            _data$userAuthTokenFo = data.userAuthTokenForExtension, success = _data$userAuthTokenFo.success, authToken = _data$userAuthTokenFo.authToken, errors = _data$userAuthTokenFo.errors;
            tracing === null || tracing === void 0 || tracing.recordGqlCall('userAuthTokenForExtension - end');
            if (!(!success && errors && errors[0].message.includes('No Atlassian OAuth token found for this user and extension'))) {
              _context4.next = 10;
              break;
            }
            return _context4.abrupt("return", new Promise(function (resolve) {
              renderThreeLOPrompt( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
                var token;
                return _regeneratorRuntime.wrap(function _callee3$(_context3) {
                  while (1) switch (_context3.prev = _context3.next) {
                    case 0:
                      _context3.next = 2;
                      return fetchOAuthToken(extensionId, contextIds);
                    case 2:
                      token = _context3.sent;
                      resolve(token);
                    case 4:
                    case "end":
                      return _context3.stop();
                  }
                }, _callee3);
              })));
            }));
          case 10:
            if (!(success && authToken)) {
              _context4.next = 13;
              break;
            }
            token = authToken.token, ttl = authToken.ttl;
            return _context4.abrupt("return", {
              token: token,
              expiry: Date.now() + ttl * MILLIS_IN_SECOND
            });
          case 13:
            throw new Error('An unexpected error occurred when fetching an auth token');
          case 14:
          case "end":
            return _context4.stop();
        }
      }, _callee4);
    }));
    return function fetchOAuthToken(_x3, _x4) {
      return _ref4.apply(this, arguments);
    };
  }();
  var cdnEnvironment = getCDNEnvironment(environment);
  return /*#__PURE__*/function () {
    var _ref6 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5(restPath, product, init, isMultipartFormData) {
      var _authTokenRef$current;
      var atlassianAccount, headers, body;
      return _regeneratorRuntime.wrap(function _callee5$(_context5) {
        while (1) switch (_context5.prev = _context5.next) {
          case 0:
            if (isProduct(product)) {
              _context5.next = 2;
              break;
            }
            throw new Error("Invalid product: ".concat(product));
          case 2:
            atlassianAccount = !accountType || accountType === 'atlassian';
            if (!(!isTokenValid(authTokenRef.current) && atlassianAccount)) {
              _context5.next = 7;
              break;
            }
            _context5.next = 6;
            return fetchOAuthToken(extensionId, contextIds);
          case 6:
            authTokenRef.current = _context5.sent;
          case 7:
            headers = new Headers(init.headers);
            headers.set('Authorization', "Bearer ".concat(authTokenRef === null || authTokenRef === void 0 || (_authTokenRef$current = authTokenRef.current) === null || _authTokenRef$current === void 0 ? void 0 : _authTokenRef$current.token));
            body = isMultipartFormData ? parseFormData(JSON.parse(init.body)) : init.body;
            if (isMultipartFormData) {
              // let the fetch api correctly set the multipart/form-data content-type
              // https://github.com/github/fetch/issues/505#issuecomment-293064470
              headers.delete('content-type');
            }
            return _context5.abrupt("return", requestProduct(createProductUrl(cdnEnvironment, product, restPath, cloudId), _objectSpread(_objectSpread({}, init), {}, {
              body: body,
              headers: headers
            })));
          case 12:
          case "end":
            return _context5.stop();
        }
      }, _callee5);
    }));
    return function (_x5, _x6, _x7, _x8) {
      return _ref6.apply(this, arguments);
    };
  }();
}